import { View } from "react-native";
import { Text } from "react-native-paper";
import { Feather } from "@expo/vector-icons";
import { colors } from "../../../theme";
import { SubmissionStatus } from "../../screens/types";
import { getExpiresIn } from "../../utils/dateUtils";

type BaseChipPointProps = {
  points: number;
  containerClass: string;
  textClass: string;
  iconColor: string;
  icon: keyof typeof Feather.glyphMap;
  iconSize?: number;
};

const BaseChipPoint = ({ points, containerClass, textClass, iconColor, icon, iconSize = 13 }: BaseChipPointProps) => (
  <View className={`border rounded py-1 flex-row justify-center items-center w-16 ${containerClass}`}>
    <Feather name={icon} size={iconSize} color={iconColor} />
    <Text className={`${textClass} ml-1 font-700`} testID="chip-point">
      {points}
    </Text>
  </View>
);

type ChipPointProps = { points: number };

const AlertPointChip = ({ points }: ChipPointProps) => (
  <BaseChipPoint
    {...{
      points,
      containerClass: "border-red",
      textClass: "text-red",
      iconColor: colors.red.DEFAULT,
      icon: "alert-triangle",
    }}
  />
);

const PendingPointChip = ({ points }: ChipPointProps) => (
  <BaseChipPoint
    {...{
      points,
      containerClass: "border-sky bg-sky-lightest",
      textClass: "text-ink-light",
      iconColor: colors.ink.light,
      icon: "clock",
    }}
  />
);

const ConfirmedPointChip = ({ points }: ChipPointProps) => (
  <BaseChipPoint
    {...{
      points,
      containerClass: "border-ink",
      textClass: "text-ink",
      iconColor: colors.ink.DEFAULT,
      icon: "check",
      iconSize: 15,
    }}
  />
);

const EXPIRING_SOON = {
  ChipComponent: AlertPointChip,
  statusLabel: "Expiring soon",
  textClass: "text-red",
  textClassDark: "text-red-dark",
};
const StatusTypes = {
  [SubmissionStatus.APPROVED]: {
    ChipComponent: ConfirmedPointChip,
    statusLabel: "Confirmed",
    textClass: "text-ink",
    textClassDark: "text-ink-darkest",
  },

  [SubmissionStatus.REJECTED]: {
    ChipComponent: AlertPointChip,
    statusLabel: "Unable to Confirm",
    textClass: "text-red",
    textClassDark: "text-red-dark",
  },
  [SubmissionStatus.PENDING]: {
    ChipComponent: PendingPointChip,
    statusLabel: "Pending Review",
    textClass: "text-ink-light",
    textClassDark: "text-ink-darkest",
  },
  [SubmissionStatus.CANCELLED]: {
    statusLabel: "Canceled",
    textClass: "text-ink",
    textClassDark: "text-ink-darkest",
  },
  [SubmissionStatus.EXPIRED]: {
    ChipComponent: AlertPointChip,
    statusLabel: "Expired",
    textClass: "text-red",
    textClassDark: "text-ink-darkest",
  },

  EXPIRING_SOON,
};

type StatusType = {
  ChipComponent?: React.FC<ChipPointProps>;
  statusLabel: string;
  textClass: string;
  textClassDark?: string;
};

export const getStatusType = (status: SubmissionStatus, expiresSoon = false): StatusType =>
  expiresSoon ? StatusTypes.EXPIRING_SOON : StatusTypes[status];

export const StatusText = ({
  status,
  expiresSoon,
  expirationDate,
}: {
  status: SubmissionStatus;
  expiresSoon: boolean;
  expirationDate: string;
}) => {
  const { textClass, statusLabel } = getStatusType(status, expiresSoon);

  return (
    <Text className={`text-small-tight font-400 mt-1 ${textClass}`}>
      {expiresSoon ? `Expiring ${getExpiresIn(expirationDate)}` : statusLabel}
    </Text>
  );
};

const ChipPoint = ({ points, status, expiresSoon }: { points: number; status: SubmissionStatus; expiresSoon: boolean }) => {
  const { ChipComponent } = getStatusType(status, expiresSoon);
  return ChipComponent ? <ChipComponent points={points} /> : null;
};

export default ChipPoint;
